import { useEffect, useState } from "react";
import { Link } from "@mui/material";
import { Trans } from "react-i18next";
import { coreToast } from "@verity/components/core";
import apiClient from "../../helpers/api-client";
import { formatFileSize } from "../../helpers/file";
import i18n from "../../i18n";
import { PORTAL_TOAST_CONTAINER_ID } from "../../portal/shared";
import SetupInstructions from "./SetupInstructions";
import SetupPageHeader from "./SetupPageHeader";
import OptionCard, { SetupOptionList } from "./OptionCard";
import DownloadButton from "./DownloadButton";
import headerStyles from "./SetupPageHeader.module.css";
import styles from "./LockdownSetupInstructions.module.css";
import AppleLogo from "../../images/portal/apple-logo.svg?react";
import WindowsLogo from "../../images/portal/windows-logo.svg?react";

type DownloadUrlType = { url: string; size?: number };

export default function LockdownSetupInstructions() {
	const [loading, setLoading] = useState(true);
	const [downloadUrls, setDownloadUrls] = useState<{
		windowsExe?: DownloadUrlType;
		windowsMsi?: DownloadUrlType;
		macIntel?: DownloadUrlType;
		macArm?: DownloadUrlType;
	}>({});
	useEffect(() => {
		(async () => {
			setLoading(true);
			const [windowsExeResult, windowsMsiResult, macIntelResult, macArmResult] = await Promise.allSettled([
				apiClient.download.getLockdownDownloadUrl("win", "exe"),
				apiClient.download.getLockdownDownloadUrl("win", "msi"),
				apiClient.download.getLockdownDownloadUrl("mac"),
				apiClient.download.getLockdownDownloadUrl("mac-arm"),
			]);
			setDownloadUrls({
				windowsExe:
					windowsExeResult.status === "fulfilled"
						? { url: windowsExeResult.value.url, size: windowsExeResult.value.size }
						: undefined,
				windowsMsi:
					windowsMsiResult.status === "fulfilled"
						? { url: windowsMsiResult.value.url, size: windowsMsiResult.value.size }
						: undefined,
				macIntel:
					macIntelResult.status === "fulfilled"
						? { url: macIntelResult.value.url, size: macIntelResult.value.size }
						: undefined,
				macArm:
					macArmResult.status === "fulfilled"
						? { url: macArmResult.value.url, size: macArmResult.value.size }
						: undefined,
			});
			setTimeout(() => setLoading(false), 500);
		})();
	}, []);

	const { windowsExe, windowsMsi, macIntel, macArm } = downloadUrls;

	const onDownload = (downloadUrl?: string) => () => {
		if (!downloadUrl) {
			coreToast.error(i18n.t("setup.components.lockdownSetupInstructionsTSX.notFound"), {
				containerId: PORTAL_TOAST_CONTAINER_ID,
			});
		}
	};

	return (
		<SetupInstructions
			header={
				<SetupPageHeader
					title={i18n.t("setup.components.lockdownSetupInstructionsTSX.title")}
					description={
						<div>
							<Trans
								i18nKey="setup.components.lockdownSetupInstructionsTSX.description"
								components={{
									a: (
										<Link
											href="https://support.yuja.com/hc/en-us/articles/10088177013527-System-Requirements-for-YuJa-Verity-for-Test-Proctoring"
											target="_blank"
										/>
									),
								}}
							/>
						</div>
					}
				>
					<iframe
						className={headerStyles.video}
						src="https://demo.video.yuja.com/V/Video?v=10401168&node=45247969&a=106252540"
						allowFullScreen
						loading="lazy"
					/>
				</SetupPageHeader>
			}
		>
			<SetupOptionList>
				<OptionCard
					title={i18n.t("setup.components.lockdownSetupInstructionsTSX.options.windows.title")}
					platformIcon={
						<div className={styles.iconWrapper}>
							<WindowsLogo />
						</div>
					}
				>
					<div className={styles.downloadOptions}>
						<div className={styles.downloadButtons}>
							<DownloadButton downloadUrl={windowsExe?.url} onClick={onDownload(windowsExe?.url)} loading={loading}>
								{i18n.t("setup.components.lockdownSetupInstructionsTSX.options.windows.downloadExe", {
									size: windowsExe?.size && !loading ? `(${formatFileSize(windowsExe?.size)})` : "",
								})}
							</DownloadButton>
							<DownloadButton downloadUrl={windowsMsi?.url} onClick={onDownload(windowsMsi?.url)} loading={loading}>
								{i18n.t("setup.components.lockdownSetupInstructionsTSX.options.windows.downloadMsi", {
									size: windowsMsi?.size && !loading ? `(${formatFileSize(windowsMsi?.size)})` : "",
								})}
							</DownloadButton>
						</div>
						<div className={styles.helpText}>
							<Trans
								i18nKey="setup.components.lockdownSetupInstructionsTSX.options.windows.singleDevice"
								components={{
									a: (
										<Link
											href="https://support.yuja.com/hc/en-us/articles/29366008174743-Understanding-if-Your-Mac-Has-an-Intel-Processor-or-Apple-M1"
											target="_blank"
										/>
									),
								}}
							/>
						</div>
					</div>
				</OptionCard>
				<OptionCard
					title={i18n.t("setup.components.lockdownSetupInstructionsTSX.options.mac.title")}
					platformIcon={
						<div className={styles.iconWrapper}>
							<AppleLogo />
						</div>
					}
				>
					<div className={styles.downloadOptions}>
						<div className={styles.downloadButtons}>
							<DownloadButton downloadUrl={macIntel?.url} onClick={onDownload(macIntel?.url)} loading={loading}>
								{i18n.t("setup.components.lockdownSetupInstructionsTSX.options.mac.downloadIntel", {
									size: macIntel?.size && !loading ? `(${formatFileSize(macIntel?.size)})` : "",
								})}
							</DownloadButton>
							<DownloadButton downloadUrl={macArm?.url} onClick={onDownload(macArm?.url)} loading={loading}>
								{i18n.t("setup.components.lockdownSetupInstructionsTSX.options.mac.downloadMSeries", {
									size: macArm?.size && !loading ? `(${formatFileSize(macArm?.size)})` : "",
								})}
							</DownloadButton>
						</div>
						<div className={styles.helpText}>
							{i18n.t("setup.components.lockdownSetupInstructionsTSX.options.mac.ifUnsure")}{" "}
							<Link
								href="https://support.yuja.com/hc/en-us/articles/29366008174743-Understanding-if-Your-Mac-Has-an-Intel-Processor-or-Apple-M1"
								target="_blank"
							>
								{i18n.t("setup.components.lockdownSetupInstructionsTSX.options.mac.learnMore")}
							</Link>
						</div>
					</div>
				</OptionCard>
			</SetupOptionList>
		</SetupInstructions>
	);
}
